import { ComponentType, lazy } from 'react';

// ErrorPage is kept as a static import since it's the catch-all fallback route
import ErrorPage from 'pages/Error/ErrorPage';
import type { RoutePermissionSet } from 'utils/permissions';

import ClientManagementRedirect from './ClientManagementRedirect';
import withLegacyClientsViewAccess from './LegacyClientsViewGuard';
import { getRoutePermissions } from './routePermissions';

const DYNAMIC_IMPORT_MESSAGES = [
  'Failed to fetch dynamically imported module', // Chrome / Edge
  'error loading dynamically imported module', // Firefox
  'Importing a module script failed', // Safari
] as const;

const isChunkLoadError = (error: unknown): error is TypeError =>
  error instanceof TypeError && DYNAMIC_IMPORT_MESSAGES.some((message) => error.message.includes(message));

// Wraps React.lazy with a retry mechanism: if a dynamic import fails (e.g. after a
// new deploy removes old chunk files), the page is automatically reloaded so the
// browser fetches the updated HTML with correct chunk references.
// A sessionStorage flag prevents infinite reload loops.
const lazyWithRetry = (importFn: () => Promise<{ default: ComponentType }>) =>
  lazy(() =>
    importFn()
      .then((module) => {
        sessionStorage.removeItem('chunk_reload');
        return module;
      })
      .catch((error: unknown) => {
        if (!isChunkLoadError(error)) {
          return Promise.reject(error);
        }

        const hasReloaded = sessionStorage.getItem('chunk_reload');
        if (!hasReloaded) {
          sessionStorage.setItem('chunk_reload', '1');
          window.location.reload();
          return new Promise<never>(() => {}); // Suspend until reload completes
        }
        sessionStorage.removeItem('chunk_reload');
        // If already reloaded once, let the error propagate to the ErrorBoundary
        return Promise.reject(error);
      })
  );

// --- Lazy-loaded page components (route-level code splitting) ---
const Activate = lazyWithRetry(() => import('pages/Activate'));
const Alerts = lazyWithRetry(() => import('pages/Alerts'));
const Analyses = lazyWithRetry(() => import('pages/Analyses'));
const Analysis = lazyWithRetry(() => import('pages/Analysis'));
const AssignmentRules = lazyWithRetry(() => import('pages/AssignmentRules'));
const BusinessRules = lazyWithRetry(() => import('pages/BusinessRules'));
const BusinessRulesByType = lazyWithRetry(() => import('pages/BusinessRules/BusinessRulesByType'));
const Brokerage = lazyWithRetry(() => import('pages/Brokerage'));
const ClaveUnicaWhatsApp = lazyWithRetry(() => import('pages/ClaveUnicaWhatsApp'));
const Client = lazyWithRetry(() => import('pages/Client'));
const ClientQuestions = lazyWithRetry(() => import('pages/ClientQuestions'));
const Clients = lazyWithRetry(() => import('pages/Clients'));
const Contact = lazyWithRetry(() => import('pages/Contact'));
const CookiesPolicy = lazyWithRetry(() => import('pages/CookiesPolicy'));
const Dashboard = lazyWithRetry(() => import('pages/Dashboard'));
const DbReader = lazyWithRetry(() => import('pages/DbReader'));
const MortgageAnalysis = lazyWithRetry(() => import('pages/MortgageAnalysis'));
const DemoChat = lazyWithRetry(() => import('pages/DemoChat'));
const NewDemoChat = lazyWithRetry(() => import('pages/DemoChat/NewChat'));
const Feedback = lazyWithRetry(() => import('pages/Feedback'));
const ForgotPassword = lazyWithRetry(() => import('pages/ForgotPassword'));
const Funnel = lazyWithRetry(() => import('pages/Funnel'));
const MobileFunnelChatView = lazyWithRetry(() => import('../pages/Funnel/mobile/MobileFunnelChatView'));
const MobileFunnelDetailsView = lazyWithRetry(() => import('../pages/Funnel/mobile/MobileFunnelDetailsView'));
const GlobalPromptSections = lazyWithRetry(() => import('pages/GlobalPromptSections'));
const Leads = lazyWithRetry(() => import('pages/Leads'));
const Looker = lazyWithRetry(() => import('pages/Looker'));
const NewClient = lazyWithRetry(() => import('pages/NewClient'));
const PartnerCenter = lazyWithRetry(() => import('pages/PartnerCenter'));
const Playground = lazyWithRetry(() => import('pages/Playground'));
const PortalInmobiliarioMock = lazyWithRetry(() => import('pages/PortalInmobiliarioMock'));
const PrivacyAndTrust = lazyWithRetry(() => import('pages/PrivacyAndTrust'));
const PrivacyPolicy = lazyWithRetry(() => import('pages/PrivacyPolicy'));
const Profile = lazyWithRetry(() => import('pages/Profile'));
const Project = lazyWithRetry(() => import('pages/Project'));
const CreateProject = lazyWithRetry(() => import('pages/Project/CreateProject'));
const ProjectClone = lazyWithRetry(() => import('pages/Project/ProjectClone'));
const PromptSections = lazyWithRetry(() => import('pages/PromptSections'));
const Properties = lazyWithRetry(() => import('pages/Properties'));
const Property = lazyWithRetry(() => import('pages/Property'));
const PropertyUpload = lazyWithRetry(() => import('pages/PropertyUpload'));
const PublicChat = lazyWithRetry(() => import('pages/PublicChat'));
const RBAC = lazyWithRetry(() => import('pages/RBAC'));
const Permission = lazyWithRetry(() => import('pages/RBAC/Permission'));
const Permissions = lazyWithRetry(() => import('pages/RBAC/Permissions'));
const Role = lazyWithRetry(() => import('pages/RBAC/Role'));
const Roles = lazyWithRetry(() => import('pages/RBAC/Roles'));
const RuleConfigsView = lazyWithRetry(() => import('pages/RuleConfigs'));
const SourceMappingRulesView = lazyWithRetry(() => import('pages/SourceMappingRules'));
const ResetPassword = lazyWithRetry(() => import('pages/ResetPassword'));
const SignIn = lazyWithRetry(() => import('pages/SignIn'));
const TemplateSender = lazyWithRetry(() => import('pages/TemplateSender'));
const Templates = lazyWithRetry(() => import('pages/Templates'));
const WhatsAppFlows = lazyWithRetry(() => import('pages/WhatsAppFlows'));
const TermsConditions = lazyWithRetry(() => import('pages/TermsConditions'));
const Users = lazyWithRetry(() => import('pages/Users'));
const WhatsAppCalls = lazyWithRetry(() => import('pages/WhatsAppCalls'));

type Sidebar = 'persistent' | 'temporary' | 'hidden';

export type Route = {
  title: string;
  mobileTitle?: string;
  path: string;
  regex?: RegExp;
  component: ComponentType;
  private: boolean;
  adminOnly?: boolean;
  index: boolean;
  noLayout?: boolean;
  sidebarType?: Sidebar;
  permissions?: RoutePermissionSet;
};

const routes: { [key: string]: Route } = {
  '/sign-up': {
    title: 'Crear Usuario',
    path: '/sign-up',
    component: SignIn,
    private: false,
    index: false,
  },
  '/sign-in': {
    title: 'Iniciar Sesión',
    path: '/sign-in',
    component: SignIn,
    private: false,
    index: false,
  },
  '/forgot-password': {
    title: 'Recuperar Contraseña',
    path: '/forgot-password',
    component: ForgotPassword,
    private: false,
    index: false,
  },
  '/reset-password': {
    title: 'Restablecer Contraseña',
    path: '/reset-password',
    component: ResetPassword,
    private: false,
    index: false,
  },
  '/activate': {
    title: 'Activar Cuenta',
    path: '/activate',
    component: Activate,
    private: false,
    index: false,
  },
  '/contact': {
    title: 'Contacto',
    path: '/contact',
    component: Contact,
    private: false,
    index: false,
  },
  '/users': {
    title: 'Usuarios',
    path: '/users',
    component: Users,
    private: true,
    adminOnly: true,
    index: false,
  },
  '/properties': {
    title: 'Propiedades',
    path: '/properties',
    component: Properties,
    private: true,
    adminOnly: true,
    index: false,
  },
  '/properties/:id': {
    title: 'Propiedad',
    path: '/properties/:id',
    regex: /\/properties\/(\d+|new)/,
    component: Property,
    private: true,
    index: false,
  },
  '/analysis': {
    title: 'Análisis',
    path: '/analysis',
    component: Analyses,
    private: true,
    permissions: getRoutePermissions('/analysis'),
    index: false,
  },
  '/analysis/:analysisId': {
    title: 'Análisis',
    path: '/analysis/:analysisId',
    regex: /\/analysis\/\d+/,
    component: Analysis,
    private: true,
    permissions: getRoutePermissions('/analysis'),
    index: false,
  },
  '/profile': {
    title: 'Perfil',
    path: '/profile',
    component: Profile,
    private: true,
    index: false,
  },
  '/project': {
    title: 'Proyecto',
    path: '/project',
    component: Project,
    private: true,
    index: false,
  },
  '/project/create': {
    title: 'Crear Proyecto',
    path: '/project/create',
    component: CreateProject,
    private: true,
    index: false,
  },
  '/project/clone': {
    title: 'Clonar Proyecto',
    path: '/project/clone',
    component: ProjectClone,
    private: true,
    index: false,
  },
  '/project/:projectId': {
    title: 'Proyecto',
    path: '/project/:projectId',
    regex: /\/project\/\d+/,
    component: Project,
    private: true,
    index: false,
  },
  '/clients': {
    title: 'Clientes',
    path: '/clients',
    component: withLegacyClientsViewAccess(Clients),
    private: true,
    index: false,
    permissions: getRoutePermissions('/clients'),
  },
  '/brokerage': {
    title: 'Brokerage',
    path: '/brokerage',
    component: Brokerage,
    private: true,
    index: false,
    permissions: getRoutePermissions('/brokerage'),
  },
  '/calls': {
    title: 'Llamadas',
    path: '/calls',
    component: WhatsAppCalls,
    private: true,
    index: false,
    permissions: getRoutePermissions('/calls'),
  },
  '/client-management': {
    title: 'Clientes',
    path: '/client-management',
    component: ClientManagementRedirect,
    private: true,
    index: false,
    noLayout: true,
  },
  '/client-management/chat/:clientId': {
    title: 'Chat de Cliente',
    path: '/client-management/chat/:clientId',
    component: ClientManagementRedirect,
    private: true,
    index: false,
    noLayout: true,
    sidebarType: 'hidden',
  },
  '/client-management/details/:clientId': {
    title: 'Detalles de Cliente',
    path: '/client-management/details/:clientId',
    component: ClientManagementRedirect,
    private: true,
    index: false,
    noLayout: true,
    sidebarType: 'hidden',
  },
  '/funnel': {
    title: 'Funnel',
    mobileTitle: 'Clientes',
    path: '/funnel',
    component: Funnel,
    private: true,
    index: false,
    permissions: getRoutePermissions('/funnel'),
  },
  '/funnel/chat/:clientId': {
    title: 'Chat de Cliente',
    path: '/funnel/chat/:clientId',
    component: MobileFunnelChatView,
    private: true,
    index: false,
    noLayout: true,
    sidebarType: 'hidden',
    permissions: getRoutePermissions('/funnel'),
  },
  '/funnel/details/:clientId': {
    title: 'Detalles de Cliente',
    path: '/funnel/details/:clientId',
    component: MobileFunnelDetailsView,
    private: true,
    index: false,
    noLayout: true,
    sidebarType: 'hidden',
    permissions: getRoutePermissions('/funnel'),
  },
  '/leads': {
    title: 'Leads',
    path: '/leads',
    component: Leads,
    private: true,
    index: false,
    permissions: getRoutePermissions('/leads'),
  },

  '/clients/new': {
    title: 'Nuevo Cliente',
    path: '/clients/new',
    component: NewClient,
    private: true,
    index: false,
  },
  '/clients/:id': {
    title: 'Cliente',
    path: '/clients/:id',
    regex: /\/clients\/\d+/,
    component: withLegacyClientsViewAccess(Client),
    private: true,
    index: false,
    permissions: getRoutePermissions('/clients'),
  },
  '/portal-inmobiliario': {
    title: 'Portal Inmobiliario',
    path: '/portal-inmobiliario',
    component: PortalInmobiliarioMock,
    private: false,
    index: false,
  },
  '/': {
    title: 'Soporte',
    path: '/',
    component: Dashboard,
    private: true,
    index: false,
  },
  // '/admin': {
  //   title: 'Administrador',
  //   path: '/admin',
  //   component: Admin,
  //   private: true,
  //   index: false,
  // },
  '/admin/projects': {
    title: 'Proyectos',
    path: '/admin/projects',
    component: Project,
    private: true,
    index: false,
  },
  '/upsert-properties': {
    title: 'Cargar Propiedades',
    path: '/upsert-properties',
    component: PropertyUpload,
    private: true,
    adminOnly: true,
    index: false,
  },
  '/playground': {
    title: 'Playground',
    path: '/playground/:messageId',
    regex: /\/playground\/\d+/,
    component: Playground,
    private: true,
    index: false,
    noLayout: true,
  },
  // '/admin/clients': {
  //   title: 'Clients',
  //   path: '/admin/chat',
  //   component: WhatsAppChat,
  //   private: true,
  //   index: false,
  // },
  '/clave-unica-whatsapp': {
    title: 'Clave Única WhatsApp',
    path: '/clave-unica-whatsapp',
    component: ClaveUnicaWhatsApp,
    private: false,
    index: false,
  },
  '/db-reader': {
    title: 'Analizador de Base de Datos',
    path: '/db-reader',
    component: DbReader,
    private: true,
    adminOnly: true,
    index: false,
    permissions: getRoutePermissions('/db-reader'),
  },
  '/mortgage-analysis': {
    title: 'Análisis Hipotecario',
    path: '/mortgage-analysis',
    component: MortgageAnalysis,
    private: true,
    adminOnly: true,
    index: false,
    permissions: getRoutePermissions('/mortgage-analysis'),
  },
  '/questions': {
    title: 'Preguntas de Clientes',
    path: '/questions',
    component: ClientQuestions,
    private: true,
    index: false,
  },
  '/demochat': {
    title: 'Demo Chat',
    path: '/demochat',
    component: DemoChat,
    private: false,
    index: false,
    noLayout: true,
  },
  '/demochat/:companyIdentifier': {
    title: 'Demo Chat',
    path: '/demochat/:companyIdentifier',
    component: DemoChat,
    private: false,
    index: false,
    noLayout: true,
  },
  '/demochat/:companyIdentifier/newchat': {
    title: 'New Demo Chat',
    path: '/demochat/:companyIdentifier/newchat',
    component: NewDemoChat,
    private: false,
    index: false,
    noLayout: true,
  },
  '/publicChat': {
    title: 'Public Chat',
    path: '/publicChat',
    component: PublicChat,
    private: false,
    index: false,
    noLayout: true,
  },
  '/terms-conditions': {
    title: 'Términos y Condiciones',
    path: '/terms-conditions',
    component: TermsConditions,
    private: false,
    index: false,
  },
  '/privacy-and-trust': {
    title: 'Privacidad y Confianza',
    path: '/privacy-and-trust',
    component: PrivacyAndTrust,
    private: false,
    index: false,
  },
  '/privacy-policy': {
    title: 'Política de Privacidad',
    path: '/privacy-policy',
    component: PrivacyPolicy,
    private: false,
    index: false,
  },
  '/cookies-policy': {
    title: 'Política de Cookies',
    path: '/cookies-policy',
    component: CookiesPolicy,
    private: false,
    index: false,
  },
  '/information': {
    title: 'Información',
    path: '/information',
    component: PromptSections,
    private: true,
    adminOnly: true,
    index: false,
  },
  '/alerts': {
    title: 'Alertas',
    path: '/alerts',
    component: Alerts,
    private: true,
    index: false,
  },
  '/feedback': {
    title: 'Queremos escucharte',
    path: '/feedback',
    component: Feedback,
    private: true,
    index: false,
  },
  '/looker': {
    title: 'Reporte',
    path: '/looker',
    component: Looker,
    private: true,
    index: false,
    permissions: getRoutePermissions('/looker'),
  },
  '/looker/:projectId': {
    title: 'Reporte',
    path: '/looker/:projectId',
    component: Looker,
    private: true,
    index: false,
    permissions: getRoutePermissions('/looker'),
  },
  '/rbac': {
    title: 'RBAC',
    path: '/rbac',
    component: RBAC,
    private: true,
    index: false,
    permissions: getRoutePermissions('/rbac'),
  },
  '/rbac/roles': {
    title: 'Gestión de Roles',
    path: '/rbac/roles',
    component: Roles,
    private: true,
    index: false,
    permissions: getRoutePermissions('/rbac'),
  },
  '/rbac/roles/:id': {
    title: 'Rol',
    path: '/rbac/roles/:id',
    component: Role,
    private: true,
    index: false,
    permissions: getRoutePermissions('/rbac'),
  },
  '/rbac/permissions': {
    title: 'Gestión de Permisos',
    path: '/rbac/permissions',
    component: Permissions,
    private: true,
    index: false,
    permissions: getRoutePermissions('/rbac'),
  },
  '/rbac/permissions/:id': {
    title: 'Permiso',
    path: '/rbac/permissions/:id',
    component: Permission,
    private: true,
    index: false,
    permissions: getRoutePermissions('/rbac'),
  },
  '/global-prompt-sections': {
    title: 'Secciones Globales',
    path: '/global-prompt-sections',
    component: GlobalPromptSections,
    private: true,
    adminOnly: true,
    index: false,
  },
  '/rule-configs': {
    title: 'Automatizaciones',
    path: '/rule-configs',
    component: RuleConfigsView,
    private: true,
    adminOnly: true,
    index: false,
  },
  '/rule-configs/:projectId': {
    title: 'Automatizaciones',
    path: '/rule-configs/:projectId',
    regex: /\/rule-configs\/\d+/,
    component: RuleConfigsView,
    private: true,
    index: false,
  },
  '/source-mapping': {
    title: 'Mapeo de orígenes',
    path: '/source-mapping',
    component: SourceMappingRulesView,
    private: true,
    adminOnly: true,
    index: false,
  },
  '/source-mapping/:projectId': {
    title: 'Mapeo de orígenes',
    path: '/source-mapping/:projectId',
    regex: /\/source-mapping\/\d+/,
    component: SourceMappingRulesView,
    private: true,
    adminOnly: true,
    index: false,
  },
  '/partner-center': {
    title: 'Partner Center',
    path: '/partner-center',
    component: PartnerCenter,
    private: true,
    index: false,
    permissions: getRoutePermissions('/partner-center'),
  },
  '/templates': {
    title: 'Templates',
    path: '/templates',
    component: Templates,
    private: true,
    index: false,
    permissions: getRoutePermissions('/templates'),
  },
  '/template-sender': {
    title: 'Mensajería',
    path: '/template-sender',
    component: TemplateSender,
    private: true,
    index: false,
    permissions: getRoutePermissions('/template-sender'),
  },
  '/whatsapp-flows': {
    title: 'WhatsApp Flows',
    path: '/whatsapp-flows',
    component: WhatsAppFlows,
    private: true,
    index: false,
    permissions: getRoutePermissions('/whatsapp-flows'),
  },
  '/business-rules': {
    title: 'Business Rules',
    path: '/business-rules',
    component: BusinessRules,
    private: true,
    adminOnly: true,
    index: false,
  },
  '/business-rules/:businessRuleTypeId': {
    title: 'Business Rules',
    path: '/business-rules/:businessRuleTypeId',
    regex: /\/business-rules\/\d+/,
    component: BusinessRulesByType,
    private: true,
    index: false,
  },
  '/assignment-rules': {
    title: 'Reglas de Asignación',
    path: '/assignment-rules',
    component: AssignmentRules,
    private: true,
    adminOnly: true,
    index: false,
  },
  // this path must be the last one
  error: {
    title: 'Error',
    path: '*',
    component: ErrorPage,
    private: true,
    index: false,
  },
};

export default routes;
